import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { PageHeader, StatusBadge, STATUS, ConfirmDelete, usePaginated, Paginator } from "@/components/common";
import JobPrint from "@/components/JobPrint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, FileText, Printer, Eye, Download, Receipt } from "lucide-react";
import { InternalDocDialog, PaidPromptDialog, DocBadge } from "@/components/InternalDocDialog";
import { toast } from "sonner";

const FILTERS = [["", "Všetky"], ["naplanovane", "Naplánované"], ["rozpracovana", "Rozpracované"], ["neuhradena", "Neuhradené"], ["dokoncena", "Uhradené"]];
const SORTS = [["created_desc", "Najnovšie"], ["created_asc", "Najstaršie"], ["number_desc", "Číslo ↓"], ["number_asc", "Číslo ↑"], ["open_first", "Neuhradené prvé"], ["done_first", "Uhradené prvé"]];
const jobTotal = (j) => (j.items || []).reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount || 0) / 100) * (1 + (j.vat_mode === "RC" ? 0 : Number(it.vat_rate || 0)) / 100), 0);
const jobNet = (j) => (j.items || []).reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount || 0) / 100), 0);
const jobHasVat = (j) => jobTotal(j) > jobNet(j) + 0.005;
const isDone = (j) => j.status === "dokoncena";

export default function Jobs() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("created_desc");
  const [hidePlanned, setHidePlanned] = useState(false);
  const [settings, setSettings] = useState({});
  const [view, setView] = useState({ job: null, print: false });
  const [prompt, setPrompt] = useState(null);
  const [docJob, setDocJob] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/jobs", { params: { q, status: filter } });
    setItems(data);
  }, [q, filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const sorted = [...items].sort((a, b) => {
    switch (sort) {
      case "created_asc": return (a.created_at || "").localeCompare(b.created_at || "");
      case "number_desc": return (b.number || "").localeCompare(a.number || "");
      case "number_asc": return (a.number || "").localeCompare(b.number || "");
      case "open_first": return (isDone(a) - isDone(b)) || (b.created_at || "").localeCompare(a.created_at || "");
      case "done_first": return (isDone(b) - isDone(a)) || (b.created_at || "").localeCompare(a.created_at || "");
      default: return (b.created_at || "").localeCompare(a.created_at || "");
    }
  });
  const visible = hidePlanned ? sorted.filter((j) => j.status !== "naplanovane") : sorted;
  const pg = usePaginated(visible);
  const changeStatus = async (j, status) => {
    if (status === "dokoncena" && !j.doc) { setPrompt(j); return; }
    await api.put(`/jobs/${j.id}`, { status }); toast.success("Stav zmenený"); load();
  };
  const promptInternal = () => { setDocJob(prompt); setPrompt(null); };
  const promptInvoice = () => { const j = prompt; setPrompt(null); navigate("/faktury/nova", { state: { fromJob: j } }); };
  const promptSkip = async () => { const j = prompt; setPrompt(null); await api.put(`/jobs/${j.id}`, { status: "dokoncena" }); toast.success("Označené ako uhradené bez dokladu"); load(); };
  const remove = async (id) => { await api.delete(`/jobs/${id}`); toast.success("Zmazané"); load(); };
  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/jobs/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `zakazka_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("PDF sa nepodarilo vygenerovať"); }
  };

  const printPdf = async (id) => {
    try {
      const res = await api.get(`/jobs/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed"; iframe.style.right = "0"; iframe.style.bottom = "0";
      iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "0";
      iframe.src = url;
      iframe.onload = () => { setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 300); };
      document.body.appendChild(iframe);
      setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 60000);
    } catch (e) { toast.error("PDF sa nepodarilo vygenerovať"); }
  };

  return (
    <div>
      <PageHeader title="Zákazky" subtitle="Správa servisných zákaziek"
        action={<Button className="min-h-[48px] gap-2" onClick={() => navigate("/zakazky/nova")} data-testid="new-job-button"><Plus className="h-5 w-5" /> Nová zákazka</Button>} />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="min-h-[48px] pl-9" placeholder="Hľadať zákazku, zákazníka, auto…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="job-search" />
        </div>
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
          {FILTERS.map(([val, lbl]) => (
            <button key={val} onClick={() => setFilter(val)} data-testid={`job-filter-${val || "all"}`}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${filter === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
          ))}
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-11 w-[180px]" data-testid="job-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <label className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-medium" data-testid="hide-planned-toggle">
          <input type="checkbox" checked={hidePlanned} onChange={(e) => setHidePlanned(e.target.checked)} className="h-4 w-4 accent-primary" />
          Skryť naplánované
        </label>
      </div>

      <div className="space-y-3">
        {visible.length ? pg.paged.map((j) => (
          <div key={j.id} className="card-surface p-4" data-testid={`job-card-${j.id}`}>
            <div className="flex flex-col gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  {j.number && <span className="font-mono text-sm font-bold text-primary">{j.number}</span>}
                  <h3 className="font-heading text-xl font-bold sm:text-2xl">{j.title}</h3>
                  <StatusBadge status={j.status} />
                  <DocBadge doc={j.doc} testid={`job-doc-badge-${j.id}`} />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                  {j.vehicle?.spz && <span className="plate">{j.vehicle.spz}</span>}
                  {j.vehicle && <span>{j.vehicle.brand} {j.vehicle.model}</span>}
                  {j.customer?.name && <span>· {j.customer.name}</span>}
                  <span className="font-mono text-foreground">{eur(jobTotal(j))} <span className="ml-0.5 text-[10px] text-muted-foreground">{jobHasVat(j) ? "s DPH" : "bez DPH"}</span></span>
                  <span>· {(j.created_at || "").slice(0, 10)}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={j.status} onValueChange={(v) => changeStatus(j, v)}>
                  <SelectTrigger className="h-9 w-[150px]" data-testid={`job-status-select-${j.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="secondary" size="sm" className="gap-1" onClick={() => navigate("/faktury/nova", { state: { fromJob: j } })} data-testid={`job-invoice-${j.id}`}><FileText className="h-3.5 w-3.5" /> Faktúra</Button>
                <Button variant="secondary" size="sm" className="gap-1" onClick={() => navigate("/faktury/nova", { state: { fromJob: j, docType: "zalohova" } })} data-testid={`job-proforma-${j.id}`}><FileText className="h-3.5 w-3.5 text-amber-300" /> Záloha</Button>
                {!j.doc && <Button variant="secondary" size="sm" className="gap-1" onClick={() => setDocJob(j)} data-testid={`job-internal-doc-${j.id}`}><Receipt className="h-3.5 w-3.5 text-violet-300" /> Interný doklad</Button>}
                <Button variant="ghost" size="icon" onClick={() => setView({ job: j, print: false })} title="Náhľad" data-testid={`preview-job-${j.id}`}><Eye className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => printPdf(j.id)} title="Tlačiť" data-testid={`print-job-${j.id}`}><Printer className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => downloadPdf(j.id)} title="Stiahnuť PDF" data-testid={`pdf-job-${j.id}`}><Download className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => navigate(`/zakazky/${j.id}/upravit`)} data-testid={`edit-job-${j.id}`}><Pencil className="h-4 w-4" /></Button>
                <ConfirmDelete onConfirm={() => remove(j.id)} description={`Zákazka „${j.title}“ bude natrvalo odstránená.`}>
                  <Button variant="ghost" size="icon" data-testid={`delete-job-${j.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </ConfirmDelete>
              </div>
            </div>
          </div>
        )) : null}
        {!visible.length && <div className="py-12 text-center text-muted-foreground">Žiadne zákazky.</div>}
      </div>

      <Paginator {...pg} />
      <PaidPromptDialog job={prompt} onClose={() => setPrompt(null)} onInternal={promptInternal} onInvoice={promptInvoice} onSkip={promptSkip} />
      <InternalDocDialog job={docJob} open={!!docJob} onOpenChange={(v) => { if (!v) { setDocJob(null); } }} onDone={load} />

      <Dialog open={!!view.job} onOpenChange={(o) => !o && setView({ job: null, print: false })}>
        <DialogContent className="max-h-[95vh] max-w-4xl overflow-y-auto bg-background p-0">
          <div className="no-print flex items-center justify-between border-b border-border p-4">
            <DialogTitle className="font-heading text-xl uppercase">{view.print ? "Tlač zákazky" : "Náhľad zákazky"}</DialogTitle>
            <Button onClick={() => view.job && printPdf(view.job.id)} className="gap-2" data-testid="job-print-btn"><Printer className="h-4 w-4" /> Tlač</Button>
          </div>
          <div className="print-area p-4"><JobPrint job={view.job} settings={settings} /></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
